import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getDeviceInfo, 
  DeviceInfo, 
  DeviceType,
  detectZoomLevel
} from '../utils/deviceDetection';

export interface UseDeviceDetectionReturn {
  deviceInfo: DeviceInfo;
  tvZoom: number; // Zoom para TV (calculado dinamicamente, 1.0 caso contrário)
  isTVZoomLoading: boolean; // Loading enquanto zoom é aplicado
  refresh: () => void;
}

/**
 * Hook para detectar dispositivo (mobile, tablet, tv, desktop)
 * Atualiza automaticamente quando a janela é redimensionada
 * REMOVIDO: Todas as ações relacionadas a TV foram removidas
 */
export function useDeviceDetection(): UseDeviceDetectionReturn {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(() => getDeviceInfo());

  const [tvZoom, setTvZoom] = useState<number>(1.0);
  // Loading começa como true se for TV (antes de qualquer renderização)
  const [isTVZoomLoading, setIsTVZoomLoading] = useState<boolean>(() => {
    const initialInfo = getDeviceInfo();
    return initialInfo.isTV;
  });
  
  // Ref para controlar se já mostrou loading nesta montagem (evita mostrar em resize/scroll)
  const loadingShownRef = useRef<boolean>(false);

  const updateDeviceInfo = useCallback(() => {
    const info = getDeviceInfo();
    setDeviceInfo(info);
    
    // Calcular zoom dinamicamente para TV
    if (info.isTV) {
      // Mostrar loading apenas na primeira vez que detectar TV nesta montagem (carregamento/recarregamento)
      if (!loadingShownRef.current) {
        setIsTVZoomLoading(true);
        loadingShownRef.current = true;
        
        // Esconder loading após 1000ms
        setTimeout(() => {
          setIsTVZoomLoading(false);
        }, 1000);
      }
      
      const detectedZoom = detectZoomLevel();
      // Calcular zoom baseado no zoom detectado do navegador
      // Se o navegador está com zoom alto, reduzir proporcionalmente
      // Exemplo: se detectar zoom de 1.5 (150%), aplicar zoom de 0.67 (1/1.5) para compensar
      const calculatedZoom = detectedZoom > 1.0 ? 1 / detectedZoom : 0.60;
      setTvZoom(calculatedZoom);
    } else {
      setTvZoom(1.0);
      setIsTVZoomLoading(false);
      loadingShownRef.current = false;
    }
  }, []);

  useEffect(() => {
    // Atualizar na montagem
    updateDeviceInfo();

    // Listener para redimensionamento
    const handleResize = () => {
      // Debounce para evitar muitas atualizações
      setTimeout(updateDeviceInfo, 100);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    // Também verificar mudanças de zoom (se disponível)
    if ('visualViewport' in window) {
      window.visualViewport?.addEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      if ('visualViewport' in window) {
        window.visualViewport?.removeEventListener('resize', handleResize);
      }
    };
  }, [updateDeviceInfo]);

  // Redirecionar para página de QR code quando detectar TV
  // Nota: useNavigate precisa ser usado dentro de um componente Router
  // Por isso vamos fazer o redirecionamento no App.tsx

  return {
    deviceInfo,
    tvZoom,
    isTVZoomLoading,
    refresh: updateDeviceInfo
  };
}
